
import { 
    AttachFile,
    Block,
    Build,
    CancelOutlined,
    Check,
    CheckCircleOutline,
    Close,
    ContentPasteSearch,
    DoDisturbOn,
    EventBusy,
    FolderOpen,
    Forum,
    HelpOutline,
    KeyboardArrowDown,
    KeyboardArrowUp,
    KeyboardDoubleArrowUp,
    LocalOffer,
    LockOpenOutlined,
    LockOutlined,
    Login,
    Logout, 
    PendingActions, 
    Person, 
    QuestionMark, 
    Remove, 
    RemoveCircleOutline, 
    ReportProblemOutlined, 
    Schedule, 
    SortByAlpha, 
    Star
} from "@mui/icons-material";
import { IMuiIcon } from "../interfaces/IMaterial";
import { IRoom } from "../interfaces/IPartnerPms";



// Http Status
export enum HttpStatusEnum {
    Unauthorized = 401,
    Forbidden = 403,
    NotFound = 404,
    NotAcceptable = 406,
    Conflict = 409,
    PreconditionFailed = 412,
    UnsupportedMediaType = 415,
    InternalServerError = 500
}



// Registration Status
export enum RegistrationStatusEnum {
    Empty = 0,
    Active = 1,
    Inactive = 2,
    Excluded = 3
}

interface IRegistrationStatusData {
    id: number;
    name: string;
    Icon: IMuiIcon;
    className: string;
    visible: boolean;
}

export const RegistrationStatusData: Record<RegistrationStatusEnum, IRegistrationStatusData> = {
    [RegistrationStatusEnum.Empty]: {
        id: RegistrationStatusEnum.Empty,
        name: 'empty',
        Icon: HelpOutline,
        className: 'color-empty',
        visible: false
    },
    [RegistrationStatusEnum.Active]: {
        id: RegistrationStatusEnum.Active,
        name: 'active',
        Icon: CheckCircleOutline,
        className: 'color-active',
        visible: true
    },
    [RegistrationStatusEnum.Inactive]: {
        id: RegistrationStatusEnum.Inactive,
        name: 'inactive',
        Icon: RemoveCircleOutline,
        className: 'color-inactive',
        visible: true
    },
    [RegistrationStatusEnum.Excluded]: {
        id: RegistrationStatusEnum.Excluded,
        name: 'excluded',
        Icon: CancelOutlined,
        className: 'color-excluded',
        visible: true
    }
}



// Default interface for status
export interface IDefaultStatusData {
    key: number;
    name: string;
    color: string;
    Icon: IMuiIcon;
    partnerId?: string | undefined;
}



// Occupancy Status
export enum OccupancyStatusEnum {
    Available = 1,
    Occupied = 2
}

export const OccupancyStatusData: Record<OccupancyStatusEnum, IDefaultStatusData> = {
    [OccupancyStatusEnum.Available]: {
        key: OccupancyStatusEnum.Available,
        name: 'available',
        color: '#43a047',
        Icon: LockOpenOutlined,
        partnerId: undefined
    },
    [OccupancyStatusEnum.Occupied]: {
        key: OccupancyStatusEnum.Occupied,
        name: 'occupied',
        color: '#d32f2f',
        Icon: LockOutlined,
        partnerId: undefined
    }
}



// Room Status
export enum RoomStatusEnum {
    ExecutionPriority = 1,
    RoomBlocking = 2,
    OwnerReservation = 3,
    VipReservation = 4,
    DoNotDisturb = 5,
    ExpectedCheckIn = 6,
    ExpectedCheckOut = 7
}

export const RoomStatusData: Record<RoomStatusEnum, IDefaultStatusData> = {
    [RoomStatusEnum.ExecutionPriority]: {
        key: RoomStatusEnum.ExecutionPriority,
        name: 'executionPriority',
        color: '#e53935',
        Icon: ReportProblemOutlined,
        partnerId: 'executionPriority'
    },
    [RoomStatusEnum.RoomBlocking]: {
        key: RoomStatusEnum.RoomBlocking,
        name: 'blocked',
        color: '#263238',
        Icon: EventBusy,
        partnerId: 'blocked'
    },
    [RoomStatusEnum.OwnerReservation]: {
        key: RoomStatusEnum.OwnerReservation,
        name: 'ownerReservation',
        color: '#37474f',
        Icon: Person,
        partnerId: 'ownerGuestType'
    },
    [RoomStatusEnum.VipReservation]: {
        key: RoomStatusEnum.VipReservation,
        name: 'vipReservation',
        color: '#f9a825',
        Icon: Star,
        partnerId: 'vipGuestType'
    },
    [RoomStatusEnum.DoNotDisturb]: {
        key: RoomStatusEnum.DoNotDisturb,
        name: 'doNotDisturb',
        color: '#283593',
        Icon: DoDisturbOn,
        partnerId: 'doNotDisturb'
    },
    [RoomStatusEnum.ExpectedCheckIn]: {
        key: RoomStatusEnum.ExpectedCheckIn,
        name: 'expectedCheckIn',
        color: '#ff8f00',
        Icon: Login,
        partnerId: 'expectedCheckIn'
    },
    [RoomStatusEnum.ExpectedCheckOut]: {
        key: RoomStatusEnum.ExpectedCheckOut,
        name: 'expectedCheckOut',
        color: '#e53935',
        Icon: Logout,
        partnerId: 'expectedCheckOut'
    }
}



// Governance Status
export enum GovernanceStatusEnum {
    Clean = 1,
    Dirty = 2,
    Inspection = 3,
    InTidying = 4,
    Maintenance = 5,
    Interdicted = 6
}

export const GovernanceStatusData: Record<GovernanceStatusEnum, IDefaultStatusData> = {
    [GovernanceStatusEnum.Clean]: {
        key: GovernanceStatusEnum.Clean,
        name: 'clean',
        color: '#4caf50',
        Icon: Check,
        partnerId: undefined
    },
    [GovernanceStatusEnum.Dirty]: {
        key: GovernanceStatusEnum.Dirty,
        name: 'dirty',
        color: '#e53935',
        Icon: Close,
        partnerId: undefined
    },
    [GovernanceStatusEnum.Inspection]: {
        key: GovernanceStatusEnum.Inspection,
        name: 'inspection',
        color: '#156BAF',
        Icon: ContentPasteSearch,
        partnerId: undefined
    },
    [GovernanceStatusEnum.InTidying]: {
        key: GovernanceStatusEnum.InTidying,
        name: 'inTidying',
        color: '#ffa000',
        Icon: Schedule,
        partnerId: undefined
    },
    [GovernanceStatusEnum.Maintenance]: {
        key: GovernanceStatusEnum.Maintenance,
        name: 'maintenance',
        color: '#6d4c41',
        Icon: Build,
        partnerId: undefined
    },
    [GovernanceStatusEnum.Interdicted]: {
        key: GovernanceStatusEnum.Interdicted,
        name: 'interdicted',
        color: '#37474f',
        Icon: Block,
        partnerId: undefined
    }
}



// Service Status
export enum ServiceStatusEnum {
    Pending = 50,
    Open = 51,
    InProgress = 52,
    Inspection = 53,
    Completed = 54,
    Canceled = 55
}

export const ServiceStatusData: Record<ServiceStatusEnum, IDefaultStatusData> = {
    [ServiceStatusEnum.Pending]: {
        key: ServiceStatusEnum.Pending,
        name: 'pending',
        color: '#68828e',
        Icon: PendingActions
    },
    [ServiceStatusEnum.Open]: {
        key: ServiceStatusEnum.Open,
        name: 'open',
        color: '#424242',
        Icon: FolderOpen
    },
    [ServiceStatusEnum.InProgress]: {
        key: ServiceStatusEnum.InProgress,
        name: 'inProgress',
        color: '#ffb300',
        Icon: Schedule
    },
    [ServiceStatusEnum.Inspection]: {
        key: ServiceStatusEnum.Inspection,
        name: 'inspection',
        color: '#156BAF',
        Icon: ContentPasteSearch
    },
    [ServiceStatusEnum.Completed]: {
        key: ServiceStatusEnum.Completed,
        name: 'completed',
        color: '#4caf50',
        Icon: Check
    },
    [ServiceStatusEnum.Canceled]: {
        key: ServiceStatusEnum.Canceled,
        name: 'canceled',
        color: '#f44336',
        Icon: Close
    }
}



// Service Priority
export enum ServicePriorityEnum {
    Empty = 0,
    Low = 100,
    Medium = 101,
    High = 102,
    Critical = 103
}

export const ServicePriorityData: Record<ServicePriorityEnum, IDefaultStatusData> = {
    [ServicePriorityEnum.Empty]: {
        key: ServicePriorityEnum.Empty,
        name: 'undefined',
        color: '#546e7a',
        Icon: QuestionMark
    },
    [ServicePriorityEnum.Low]: {
        key: ServicePriorityEnum.Low,
        name: 'low',
        color: '#43a047',
        Icon: KeyboardArrowDown
    },
    [ServicePriorityEnum.Medium]: {
        key: ServicePriorityEnum.Medium,
        name: 'medium',
        color: '#ffa000',
        Icon: Remove
    },
    [ServicePriorityEnum.High]: {
        key: ServicePriorityEnum.High,
        name: 'high',
        color: '#e53935',
        Icon: KeyboardArrowUp
    },
    [ServicePriorityEnum.Critical]: {
        key: ServicePriorityEnum.Critical,
        name: 'critical',
        color: '#8e24aa',
        Icon: KeyboardDoubleArrowUp
    }
}



// Default Properties
interface IDefaultPropertyData extends IDefaultStatusData {
    className: string;
    type: 'array' | 'boolean' | 'number';
}


// Governance log properties
export enum GovernanceLogPropertyEnum {
    Attachments = 1
}

export const GovernanceLogPropertyData: Record<GovernanceLogPropertyEnum, IDefaultPropertyData> = {
    [GovernanceLogPropertyEnum.Attachments]: {
        key: GovernanceLogPropertyEnum.Attachments,
        name: 'attachments',
        color: 'rgba(255, 255, 255, 0.2)',
        Icon: AttachFile,
        partnerId: 'attachments',
        className: '',
        type: 'array'
    }
}



// Service properties
export enum ServicePropertyEnum {
    Attachments = 1,
    Comments = 2,
    Tags = 3
}

export const ServicePropertyData: Record<ServicePropertyEnum, IDefaultPropertyData> = {
    [ServicePropertyEnum.Attachments]: {
        key: ServicePropertyEnum.Attachments,
        name: 'attachments',
        color: 'rgba(255, 255, 255, 0.2)',
        Icon: AttachFile,
        partnerId: 'attachments',
        className: '',
        type: 'array'
    },
    [ServicePropertyEnum.Comments]: {
        key: ServicePropertyEnum.Comments,
        name: 'comments',
        color: 'rgba(255, 255, 255, 0.2)',
        Icon: Forum,
        partnerId: 'comments',
        className: 'gap-4',
        type: 'array'
    },
    [ServicePropertyEnum.Tags]: {
        key: ServicePropertyEnum.Tags,
        name: 'tags',
        color: 'rgba(255, 255, 255, 0.2)',
        Icon: LocalOffer,
        partnerId: 'tags',
        className: 'gap-4',
        type: 'array'
    }
}



// Logs
interface IDefaultLogData {
    key: string;
    name: string;
}



// Room log types
export enum RoomLogTypeEnum {
    UpdateGovernanceStatus = "updateGovernanceStatus",
	UpdateDoNotDisturb = "updateDoNotDisturb",
	UpdateExecutionPriority = "updateExecutionPriority"
}

export const RoomLogTypeData: Record<RoomLogTypeEnum, IDefaultLogData> = {
    [RoomLogTypeEnum.UpdateGovernanceStatus]: {
        key: RoomLogTypeEnum.UpdateGovernanceStatus,
        name: 'changingGovernanceStatus'
    },
    [RoomLogTypeEnum.UpdateDoNotDisturb]: {
        key: RoomLogTypeEnum.UpdateDoNotDisturb,
        name: 'changingDoNotDisturbStatus'
    },
    [RoomLogTypeEnum.UpdateExecutionPriority]: {
        key: RoomLogTypeEnum.UpdateExecutionPriority,
        name: 'changingExecutionPriority'
    }
}



// Service Log Types
export enum ServiceOrderLogTypeEnum {
    UpdateStatus = "updateStatus",
	UpdatePriority = "updatePriority",
	UpdateDepartment = "updateDepartment",
    UpdateResponsible = "updateResponsible",
    UpdateStartDate = "updateStartDate",
    UpdateEstimatedCompletionDate = "updateEstimatedCompletionDate"
}

export const ServiceOrderLogTypeData: Record<ServiceOrderLogTypeEnum, IDefaultLogData> = {
    [ServiceOrderLogTypeEnum.UpdateStatus]: {
        key: ServiceOrderLogTypeEnum.UpdateStatus,
        name: 'changingStatus'
    },
    [ServiceOrderLogTypeEnum.UpdatePriority]: {
        key: ServiceOrderLogTypeEnum.UpdatePriority,
        name: 'changingPriority'
    },
    [ServiceOrderLogTypeEnum.UpdateDepartment]: {
        key: ServiceOrderLogTypeEnum.UpdateDepartment,
        name: 'changingDepartment'
    },
    [ServiceOrderLogTypeEnum.UpdateResponsible]: {
        key: ServiceOrderLogTypeEnum.UpdateResponsible,
        name: 'changingResponsible'
    },
    [ServiceOrderLogTypeEnum.UpdateStartDate]: {
        key: ServiceOrderLogTypeEnum.UpdateStartDate,
        name: 'changingStartDate'
    },
    [ServiceOrderLogTypeEnum.UpdateEstimatedCompletionDate]: {
        key: ServiceOrderLogTypeEnum.UpdateEstimatedCompletionDate,
        name: 'changingEstimatedCompletionDate'
    }
}


// Sort by
interface IDefaultSortByData<T> {
    key: string;
    name: string;
    Icon?: IMuiIcon;
    sort: (data: T[]) => T[];
}


// Partner PMS sort by
export enum PartnerPmsSortByEnum {
    Description = "description",
	ExecutionPriority = "executionPriority"
}

export const PartnerPmsSortByData: Record<PartnerPmsSortByEnum, IDefaultSortByData<IRoom>> = {
    [PartnerPmsSortByEnum.Description]: {
        key: PartnerPmsSortByEnum.Description,
        name: 'description',
        Icon: SortByAlpha,
        sort: (data: IRoom[]) => data.sort((a, b) => a.description.localeCompare(b.description))
    },
    [PartnerPmsSortByEnum.ExecutionPriority]: {
        key: PartnerPmsSortByEnum.ExecutionPriority,
        name: 'executionPriority',
        Icon: ReportProblemOutlined,
        sort: (data: IRoom[]) => data.sort((a, b) => a.executionPriority === b.executionPriority ? 0 : (a.executionPriority ? -1 : 1))
    }
}